import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { compact, find, isEmpty } from "lodash/fp";

import { WizardFormValues } from "@app/analytics/account/WorkspaceSettings/WorkspaceWizard/types";
import * as accountApi from "@app/common/account/api";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import { createTeam } from "@app/common/workspaces/api";
import * as Events from "@app/common/workspaces/events";
import {
  EnterpriseRole,
  EnterpriseTeam,
  EnterpriseWorkspaceInviteResponse,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

const createTeamsForWorkspace = async (
  workspaceId: string,
  teams: WizardFormValues["teams"]
) => {
  const createdTeams = await Promise.all(
    teams.map(async (team) => {
      try {
        return createTeam(workspaceId, team.name);
      } catch (e) {
        Sentry.captureException(e);
        return Promise.resolve(null);
      }
    })
  );

  return compact(createdTeams);
};

const inviteAllWorkspaceUsers = async (
  workspaceId: string,
  teams: EnterpriseTeam[],
  invitees: WizardFormValues["users"]
): Promise<EnterpriseWorkspaceInviteResponse | undefined> => {
  if (isEmpty(invitees)) return;

  const invites = invitees.map((invitee) => {
    const createdTeam = invitee.team
      ? find({ name: invitee.team }, teams)
      : undefined;

    const teamRole = createdTeam
      ? ((invitee.role || "member") as EnterpriseRole)
      : undefined;

    return {
      email: invitee.email,
      teamId: createdTeam?.id,
      teamRole,
    };
  });

  return apiClient.workspace.inviteWorkspaceUsers(workspaceId, invites);
};

const createWorkspace = async (formValues: WizardFormValues) => {
  const { workspaceName, teams, users, settings } = formValues;

  const workspace = await accountApi.createWorkspace(workspaceName, settings);
  Events.createWorkspace({ workspaceId: workspace.id });

  const newTeams = await createTeamsForWorkspace(workspace.id, teams);

  const inviteResponse = await inviteAllWorkspaceUsers(
    workspace.id,
    newTeams,
    users
  );

  // team creation errors are absorbed in order to be able to invite
  // workspace members, but we still want to show an error message in
  // the UI if anything failed to create properly. this error will be
  // caught below
  const errors = [];
  if (newTeams.length !== teams.length) {
    errors.push({ message: "teams failed to be created" });
    Sentry.captureMessage("Workspace team creation failed", {
      extra: {
        expected: teams.length,
        actual: newTeams.length,
        workspaceId: workspace.id,
      },
    });
  }

  if (!isEmpty(inviteResponse?.failures)) {
    errors.push({ message: "invites failed to send" });
    Sentry.captureMessage("Workspace invites failed", {
      extra: { failures: JSON.stringify(inviteResponse?.failures) },
    });
  }

  return { workspace, errors };
};

const useCreateWorkspace = () => {
  const queryClient = useQueryClient();
  const workspaceContext = useWorkspace();
  const { fetchAndSetUser } = useAuth();

  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: ({ workspace }) => workspaceContext.setWorkspaceId(workspace.id),
    onError: (error) => {
      Sentry.captureException(error);
    },
    onSettled: async () => {
      await fetchAndSetUser();
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });
};

export default useCreateWorkspace;
